import styled from 'styled-components'

import { Polygon as PolygonComponent } from 'components/polygon'
import { theme } from 'styles/theme'

const { colors, fontSizes, media, radii, space, zIndices } = theme

export const Hero = styled.div`
  position: relative;
  width: 100%;
  min-height: 65rem;
  overflow: hidden;
  background-color: ${colors['cashme-dark-blue']};

  @media (${media.tablet}) {
    min-height: 36rem;
  }

  @media (${media.desktop}) {
    min-height: 45rem;
  }
`

export const Background = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 0;
  width: 100%;
  height: 65rem;
  overflow: hidden;

  @media (${media.tablet}) {
    height: 32rem;
  }

  @media (${media.desktop}) {
    height: 40rem;
  }

  /* Carousel wrapper (Swiper) must fill the hero */
  > div {
    height: 100%;
  }

  .swiper,
  .swiper-wrapper,
  .swiper-slide {
    height: 100%;
  }

  && .swiper-fade .swiper-slide {
    position: relative;
    opacity: 0 !important;
    transition: opacity 1.5s ease-in 1.5s !important;
  }

  && .swiper:not(.swiper-fade) .swiper-slide:not(:first-child) {
    opacity: 0;
  }

  && .swiper-fade .swiper-slide-active {
    opacity: 1 !important;
    transition: opacity 1.5s ease-in-out 1.5s !important;
  }

  && .swiper-fade .swiper-slide img {
    transform: scale(1.1);
    transition: transform 2.5s ease-out;
  }

  && .swiper-fade .swiper-slide-active img {
    transform: scale(1);
    transition: none;
  }
`

export const MobileBackground = styled(Background)`
  @media (${media.tablet}) {
    display: none;
  }
`

export const TextWrapper = styled.div`
  position: relative;
  z-index: ${zIndices[2]};
  display: flex;
  flex-direction: column;
  justify-content: end;
  padding-top: ${space['spacing-xl']};
  padding-bottom: ${space['spacing-xxl']};
  gap: ${space['spacing-sm']};

  > h1 {
    margin-bottom: ${space['spacing-md']};
    font-size: ${fontSizes['font-size-mobile-heading-2']};
    text-align: left;

    > span {
      font-size: ${fontSizes['font-size-mobile-heading-2']};
    }
  }

  > p {
    width: 100%;
    font-size: ${fontSizes['font-size-mobile-heading-4']};
  }

  @media (${media.phone}) {
    > h1 {
      padding-right: 6rem;
    }

    > p {
      padding-right: 6rem;
      font-size: ${fontSizes['font-size-body-small']};
    }
  }

  @media (${media.tablet}) {
    > h1 {
      max-width: 40rem;
      font-size: ${fontSizes['font-size-heading-1']};

      > span {
        font-size: ${fontSizes['font-size-heading-1']};
      }
    }

    > p {
      max-width: 40rem;
    }
  }

  @media (${media.desktop}) {
    padding: 7.5rem 8.5rem 6.5rem;
  }
`

export const SimulateButton = styled.div`
  position: relative;
  z-index: ${zIndices[1]};
  align-self: flex-start;
  margin-top: ${space['spacing-sm']};

  @media (${media.phone}) {
    margin-top: ${space['spacing-md']};
  }

  button.ds-button {
    padding: 0 40px;
    gap: ${space['spacing-xxs']};
    background-color: ${colors['cashme-magenta']};
    border-radius: ${radii['corner-radius-pill']};

    &:hover {
      background-color: ${colors['cashme-magenta-alt']};
    }
  }
`

export const DecorationTriangle = styled(PolygonComponent)`
  position: absolute;
`

export const Connector = styled.div`
  display: none;

  @media (${media.desktop}) {
    position: absolute;
    top: 50%;
    left: 100%;
    display: block;
    pointer-events: none;

    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 25rem;
      height: 15rem;
      border-top: 3px solid ${colors['cyan-text-contrast']};
      border-right: 3px solid ${colors['cyan-text-contrast']};
      border-top-right-radius: 100px;
    }
  }

  ${DecorationTriangle} {
    top: -1.6rem;
    left: 15rem;
    width: 44px;
    height: 56px;
    z-index: ${zIndices[1]};
  }
`

export const MobileTrustBar = styled.div`
  width: 100vw;
  margin-top: ${space['spacing-md']};
  margin-left: 50%;
  transform: translateX(-50%);
`

export const TrustBarWrapper = styled.div`
  position: absolute;
  bottom: 0;
  left: 50%;
  z-index: ${zIndices[2]};
  width: 100%;
  transform: translateX(-50%);

  @media (${media.tablet}) {
    width: calc(100% - ${space['spacing-xl']} * 2);
    max-width: 60rem;
  }
`

export const ImageOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: ${zIndices[1]};
  pointer-events: none;
  background: linear-gradient(270deg, transparent 0%, ${colors['cashme-dark-blue']} 100%);
`

export const MobileImageOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: ${zIndices[1]};
  pointer-events: none;
  background: linear-gradient(0deg, transparent 45%, ${colors['cashme-dark-blue']} 95%);
`

export const MobileDecoration = styled.div`
  display: none;

  @media (${media.phone}) {
    display: block;
    position: absolute;
    inset: 0 0 13rem;
    z-index: 0;
    pointer-events: none;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: ${space['spacing-lg']};
    width: clamp(9.4rem, 70vw, 80rem);
    height: 100%;
    border-bottom: 3px solid ${colors['cyan-text-contrast']};
    border-right: 3px solid ${colors['cyan-text-contrast']};
    border-bottom-right-radius: 100px;
  }

  ${DecorationTriangle} {
    top: ${space['spacing-md']};
    right: 0.8rem;
    width: ${space['spacing-xl']};
    height: ${space['spacing-xxl']};
  }
`
